import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { Flex, Button } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { MdKeyboardArrowDown } from 'react-icons/md';
import useOutsideClick from '../../../hooks/useOutsideClick';
import CustomCheckbox from '../../analytics/components/CustomCheckbox';

interface DropdownItem {
  id: string;
  name: string;
}

interface MultiSelectDropdownProps {
  items: DropdownItem[];
  selectedItems: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  selectAllLabel: string;
  emptyLabel: string;
  classes?: string;
  position?: 'auto' | 'above' | 'below';
  usePortal?: boolean;
  width?: string | { base: string; sm?: string; md?: string; lg?: string };
  btnClass?: string;
  clearError?: (name: string, error: boolean) => void;
  showSearch?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  items,
  selectedItems,
  onSelectionChange,
  placeholder,
  searchPlaceholder,
  selectAllLabel,
  emptyLabel,
  classes,
  btnClass,
  clearError,
  position = 'auto',
  usePortal = false,
  width = '480px',
  showSearch = false,
}) => {
  const { formatMessage } = useIntl();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedItems, setTempSelectedItems] = useState<string[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below'>(
    'below'
  );
  const [isMobileSafari, setIsMobileSafari] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Media queries for responsive width
  const isSmall =  useMediaQuery('(min-width: 640px)');
  const isMedium = useMediaQuery('(min-width: 768px)');
  const isLarge = useMediaQuery('(min-width: 1024px)');
  
  // Helper function to get current width based on screen size
  const getCurrentWidth = () => {
    if (typeof width === 'string') {
      return width;
    }
    
    if (isLarge && width.lg) return width.lg;
    if (isMedium && width.md) return width.md;
    if (isSmall && width.sm) return width.sm;
    return width.base;
  };
  
  const currentWidth = getCurrentWidth();
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedItems) {
      setTempSelectedItems(selectedItems);
      clearError?.('event_ids', false);
    }
  }, [selectedItems, clearError]);

  // Detect mobile Safari and track keyboard height
  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isMobile = /iPhone|iPad|iPod/.test(userAgent);
    setIsMobileSafari(isSafari && isMobile);

    // Track viewport height changes (keyboard appearance)
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const initialHeight = window.screen.height;
      const heightDifference = initialHeight - currentHeight;
      
      // If height difference is significant, keyboard is likely open
      if (heightDifference > 150) {
        setKeyboardHeight(heightDifference);
      } else {
        setKeyboardHeight(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate dropdown position and styles
  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Adjust for mobile Safari keyboard
    const adjustedViewportHeight = isMobileSafari && keyboardHeight > 0 
      ? viewportHeight + keyboardHeight 
      : viewportHeight;
    
    const spaceBelow = adjustedViewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let newPosition: 'above' | 'below';

    if (position === 'auto') {
      // On mobile Safari with keyboard, prefer positioning above to avoid keyboard overlap
      if (isMobileSafari && keyboardHeight > 0) {
        newPosition = 'above';
      } else if (spaceAbove > spaceBelow || spaceBelow < 200) {
        newPosition = 'above';
      } else {
        newPosition = 'below';
      }
    } else {
      newPosition = position;
    }

    setDropdownPosition(newPosition);

    // Calculate styles for portal positioning
    if (usePortal) {
      const isMobile = viewportWidth < 768;
      const styles: React.CSSProperties = {
        position: 'fixed',
        zIndex: 9999,
        width: buttonRect.width,
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        borderTopWidth: '0.5px',
        borderRightWidth: '0.5px',
        borderBottomWidth: '1px',
        borderLeftWidth: '0.5px',
        borderStyle: 'solid',
        borderColor: '#d1d5db',
        paddingTop: '8px',
        paddingRight: '12px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        gap: '8px',
        opacity: 1,
        height: 'auto',
        maxHeight: isMobile ? '200px' : '375px',
        overflowY: 'visible', // No internal scrolling
      };

      // Calculate horizontal positioning - align with button by default
      const dropdownWidth = buttonRect.width;
      const buttonLeft = buttonRect.left;
      const buttonRight = buttonRect.right;
      
      // Default: align dropdown left edge with button left edge
      let leftPosition = buttonLeft;
      
      // Check if dropdown would go off the right edge
      if (leftPosition + dropdownWidth > viewportWidth - 16) {
        // Try to align dropdown right edge with button right edge
        leftPosition = buttonRight - dropdownWidth;
        
        // If that would go off the left edge, center it
        if (leftPosition < 16) {
          leftPosition = Math.max(16, (viewportWidth - dropdownWidth) / 2);
        }
      }
      
      // Ensure minimum margin from viewport edge
      leftPosition = Math.max(16, leftPosition);
      
      styles.left = `${leftPosition}px`;
      delete styles.right;

      if (newPosition === 'above') {
        styles.bottom = `${adjustedViewportHeight - buttonRect.top + 4}px`;
      } else {
        // On mobile Safari with keyboard, ensure dropdown doesn't go below visible area
        const maxTop = isMobileSafari && keyboardHeight > 0 
          ? viewportHeight - keyboardHeight - 100 // Leave some margin
          : viewportHeight;
        
        const calculatedTop = buttonRect.bottom + 4;
        styles.top = `${Math.min(calculatedTop, maxTop)}px`;
      }

      setDropdownStyles(styles);
    }
  }, [isMobileSafari, keyboardHeight, position, usePortal]);

  // Recalculate position when dropdown becomes visible
  useEffect(() => {
    if (showDropdown && usePortal) {
      calculatePosition();
    }
  }, [showDropdown, usePortal, calculatePosition]);

  // Custom outside click handler for portal
  useEffect(() => {
    if (!usePortal || !showDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is inside the button or dropdown content
      const isInsideButton = buttonRef.current?.contains(target);
      const isInsideDropdown = dropdownContentRef.current?.contains(target);

      // Check if the target is the search input or inside the dropdown items
      const isSearchInput = target.tagName === 'INPUT' && dropdownContentRef.current?.contains(target);

      // Don't close if clicking inside dropdown (including search input)
      if (isInsideButton || isInsideDropdown || isSearchInput) {
        return;
      }

      setShowDropdown(false);
    };

    const handleResize = () => {
      if (showDropdown) {
        calculatePosition();
      }
    };

    // Use mousedown instead of click, and don't use capture phase
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [usePortal, showDropdown, calculatePosition]);

  // Use the original useOutsideClick for non-portal mode
  const dropdownRef = useOutsideClick(() => {
    if (!usePortal) {
      setShowDropdown(false);
    }
  });

  // Filter items based on search term
  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get display text for button


  // Handle individual item toggle
  const handleItemToggle = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event bubbling
    const newSelectedItems = tempSelectedItems.includes(itemId)
      ? tempSelectedItems.filter((id) => id !== itemId)
      : [...tempSelectedItems, itemId];
    setTempSelectedItems(newSelectedItems);
    onSelectionChange(newSelectedItems);
    clearError?.('event_ids', false);
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    const allFilteredIds = filteredItems.map((item) => item.id);
    const allSelected = allFilteredIds.every((id) =>
      tempSelectedItems.includes(id)
    );
    const newSelectedItems = allSelected
      ? tempSelectedItems.filter((id) => !allFilteredIds.includes(id))
      : [...new Set([...tempSelectedItems, ...allFilteredIds])];
    setTempSelectedItems(newSelectedItems);
    onSelectionChange(newSelectedItems);
  };


  // Initialize temp state when dropdown opens
  const handleDropdownToggle = () => {
    if (showDropdown) {
      setShowDropdown(false);
    } else {
      setShowDropdown(true);
      setSearchTerm('');
      setTempSelectedItems([...selectedItems]);
      // Calculate position after a small delay to ensure DOM is updated
      setTimeout(() => {
        calculatePosition();
      }, 0);
    }
  };


  // Get dropdown positioning styles for non-portal mode
  const getDropdownStyles = () => {
    const baseStyles = {
      position: 'absolute' as const,
      right: 0,
      zIndex: 9999,
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    };

    if (dropdownPosition === 'above') {
      return {
        ...baseStyles,
        bottom: '100%',
        marginBottom: '4px',
      };
    } else {
      return {
        ...baseStyles,
        top: '100%',
        marginTop: '4px',
      };
    }
  };

  // Get container styles based on position for non-portal mode
  const getContainerStyles = () => {
    const baseStyles = {
      width: buttonRef.current?.offsetWidth || currentWidth,
      borderRadius: '8px',
      borderTopWidth: '0.5px',
      borderRightWidth: '0.5px',
      borderBottomWidth: '1px',
      borderLeftWidth: '0.5px',
      borderStyle: 'solid' as const,
      borderColor: '#d1d5db',
      paddingTop: '8px',
      paddingRight: '12px',
      paddingBottom: '8px',
      paddingLeft: '12px',
      gap: '8px',
      opacity: 1,
    };

    // Always remove maxHeight constraint to allow full expansion
    return {
      ...baseStyles,
      height: 'auto',
      maxHeight: 'none',
      overflowY: 'visible' as const,
    };
  };

  // Render dropdown content
  const renderDropdownContent = () => (
    <Flex
      direction={'column'}
      className="container-select"
      style={usePortal ? dropdownStyles : getContainerStyles()}
      ref={usePortal ? dropdownContentRef : undefined}
    >
      {/* Search Input */}
      {showSearch && (
        <input
          type="text"
          data-dropdown-search="true"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.trimStart())}
          style={{
            width: '100%',
            height: isMobileSafari ? '44px' : '30px', // Larger touch target on mobile
            borderRadius: '4px',
            paddingTop: isMobileSafari ? '12px' : '4px',
            paddingRight: '8px',
            paddingBottom: isMobileSafari ? '12px' : '4px',
            paddingLeft: '8px',
            backgroundColor: '#FAFAFA',
            border: 'none',
            fontSize: isMobileSafari ? '16px' : '13px', // Prevent zoom on iOS
            outline: 'none',
            textAlign: 'right',
            opacity: 1,
            WebkitAppearance: 'none', // Remove iOS styling
            appearance: 'none',
            // Ensure input is visible above keyboard
            position: 'relative',
            zIndex: 10000,
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onFocus={(e) => {
            e.stopPropagation();
            // On mobile, scroll input to center of screen
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
              const scrollInputToCenter = () => {
                const rect = e.target.getBoundingClientRect();
                const absoluteTop = rect.top + window.scrollY;
                const windowHeight = window.innerHeight;

                // Calculate position to center the input
                // Account for keyboard height (roughly 40% of screen)
                const visibleHeight = windowHeight * 0.6;
                const targetScrollTop = absoluteTop - (visibleHeight / 2) + (rect.height / 2);

                window.scrollTo({
                  top: Math.max(0, targetScrollTop),
                  behavior: 'smooth'
                });
              };

              // Multiple attempts to ensure input stays centered
              setTimeout(scrollInputToCenter, 100);
              setTimeout(scrollInputToCenter, 300);
              setTimeout(scrollInputToCenter, 500);
            }
          }}
          onBlur={(e) => {
            // On mobile, scroll to top when keyboard closes
            const isMobile = window.innerWidth < 768;
            const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

            if (isMobile && isIOS) {
              const forceScrollToTop = () => {
                // Force scroll to 0 first
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;

                // Check and fix iOS 26 visualViewport offset bug
                if (window.visualViewport) {
                  const offsetTop = window.visualViewport.offsetTop;
                  if (offsetTop > 0) {
                    // If offset exists, we need to scroll up more to compensate
                    window.scrollTo(0, 0);
                    window.scrollBy(0, -offsetTop);
                  }
                }

                // Final attempt with requestAnimationFrame to ensure it sticks
                requestAnimationFrame(() => {
                  window.scrollTo(0, 0);
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;

                  // Double check viewport offset one more time
                  if (window.visualViewport && window.visualViewport.offsetTop > 0) {
                    window.scrollBy(0, -window.visualViewport.offsetTop);
                  }
                });
              };

              // Immediate attempt
              forceScrollToTop();

              // Multiple attempts after keyboard closes with longer delays
              setTimeout(forceScrollToTop, 50);
              setTimeout(forceScrollToTop, 100);
              setTimeout(forceScrollToTop, 200);
              setTimeout(forceScrollToTop, 300);
              setTimeout(forceScrollToTop, 500);
              setTimeout(forceScrollToTop, 800);
              setTimeout(forceScrollToTop, 1000);
            }
          }}
        />
      )}

      {/* Items List */}
      <Flex
        direction={'column'}
        gap={'8px'}
        style={{
          flex: 1,
          maxHeight: '180px',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
        className="list-select"
      >
        {/* Select All Option */}
        <Flex
          align="center"
          gap="8px"
          style={{
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleSelectAll();
          }}
        >
          <CustomCheckbox
            checked={
              filteredItems.length > 0 &&
              filteredItems.every((item) => tempSelectedItems.includes(item.id))
            }
            indeterminate={
              filteredItems.length > 0 &&
              filteredItems.some((item) =>
                tempSelectedItems.includes(item.id)
              ) &&
              !filteredItems.every((item) =>
                tempSelectedItems.includes(item.id)
              )
            }
          />
          <span
            style={{
              fontWeight: 400,
              fontSize: '13px',
              lineHeight: '22px',
              letterSpacing: '0%',
              textAlign: 'right',
            }}
          >
            {selectAllLabel}
          </span>
        </Flex>

        {/* Individual Items */}
        {filteredItems.map((item) => (
          <Flex
            key={item.id}
            align="center"
            gap="8px"
            style={{
              cursor: 'pointer',
              backgroundColor: 'transparent',
            }}
            onClick={(e) => handleItemToggle(item.id, e)}
          >
            <CustomCheckbox checked={tempSelectedItems.includes(item.id)} />
            <span
              style={{
                fontFamily: 'Baloo Bhaijaan 2',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '22px',
                letterSpacing: '0%',
                textAlign: 'right',
              }}
            >
              {item.name}
            </span>
          </Flex>
        ))}

        {filteredItems.length === 0 && (
          <span
            style={{
              fontSize: '13px',
              textAlign: 'center',
              color: '#666',
              padding: '16px',
            }}
          >
            {emptyLabel}
          </span>
        )}
      </Flex>
    </Flex>
  );

  return (
    <div
      ref={usePortal ? undefined : dropdownRef}
      style={{ position: 'relative' }}
    >
      <Button
        ref={buttonRef}
        className={`btn-date btn-filter ${btnClass}`}
        style={{
          width: typeof width === 'object' ? currentWidth : 'fit-content',
          height: '34px',
          padding: '4px 12px !important',
          minWidth: 'unset',
          color: '#000000',
        }}
        onClick={handleDropdownToggle}
      >
        <Flex gap={'8px'} w={'100%'} align="center" justify="space-between" style={{ width: '100%' }}>
          <span
            style={{
              flex: 1,
              overflow: 'visible',
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {tempSelectedItems.length > 0
              ? tempSelectedItems.length + ' ' + formatMessage({ id: 'selected' })
              : placeholder}
          </span>
          <MdKeyboardArrowDown style={{ flexShrink: 0 }} />
        </Flex>
      </Button>

      {showDropdown &&
        (usePortal ? (
          createPortal(
            <div className={classes ? classes : ''} style={dropdownStyles}>
              {renderDropdownContent()}
            </div>,
            document.body
          )
        ) : (
          <div className={classes ? classes : ''} style={getDropdownStyles()}>
            {renderDropdownContent()}
          </div>
        ))}
    </div>
  );
};

export default MultiSelectDropdown;
